import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Lightbulb, 
  Wand2, 
  Copy, 
  Download, 
  Edit3, 
  RefreshCw,
  Mail,
  FileText,
  MessageSquare,
  Video,
  Image as ImageIcon,
  Globe,
  CheckCircle,
  Clock,
  Target,
  BarChart3
} from 'lucide-react';
import { generateContentIdeas } from '../../../services/openai';
import Icon from '../../../components/AppIcon';


const ContentGenerator = ({ funnelData }) => {
  const [selectedContentType, setSelectedContentType] = useState('email');
  const [selectedStage, setSelectedStage] = useState('awareness');
  const [customPrompt, setCustomPrompt] = useState('');
  const [generatedContent, setGeneratedContent] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [savedContent, setSavedContent] = useState([]);

  const contentTypes = [
    { id: 'email', label: 'Email Campaigns', icon: Mail, color: 'bg-blue-500' },
    { id: 'blog', label: 'Blog Posts', icon: FileText, color: 'bg-green-500' },
    { id: 'social', label: 'Social Media', icon: MessageSquare, color: 'bg-purple-500' },
    { id: 'video', label: 'Video Scripts', icon: Video, color: 'bg-red-500' },
    { id: 'ads', label: 'Ad Copy', icon: ImageIcon, color: 'bg-orange-500' },
    { id: 'landing', label: 'Landing Pages', icon: Globe, color: 'bg-teal-500' }
  ];

  const funnelStages = [
    { id: 'awareness', label: 'Awareness', description: 'Introduce your brand and value proposition' },
    { id: 'consideration', label: 'Consideration', description: 'Educate and build trust with prospects' },
    { id: 'conversion', label: 'Conversion', description: 'Drive action and sales' },
    { id: 'retention', label: 'Retention', description: 'Keep customers engaged and loyal' }
  ];

  const generateContent = async () => {
    if (!selectedContentType || !selectedStage) {
      alert('Please select content type and funnel stage');
      return;
    }

    setIsGenerating(true);
    try {
      const audienceDescription = funnelData?.targetAudience || 'general audience';
      const businessType = funnelData?.businessType || 'business';
      
      let prompt;
      if (customPrompt?.trim()) {
        prompt = customPrompt;
      } else {
        prompt = `Generate ${selectedContentType} content for the ${selectedStage} stage of a ${businessType} marketing funnel targeting ${audienceDescription}`;
      }

      const content = await generateContentIdeas(selectedContentType, selectedStage, audienceDescription);
      
      setGeneratedContent({
        type: selectedContentType,
        stage: selectedStage,
        content: content?.content_ideas,
        timestamp: new Date()?.toISOString()
      });
    } catch (error) {
      console.error('Error generating content:', error);
      alert('Failed to generate content. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const saveContent = (contentItem) => {
    const savedItem = {
      id: Date.now(),
      ...contentItem,
      type: selectedContentType,
      stage: selectedStage,
      savedAt: new Date()?.toISOString()
    };
    setSavedContent(prev => [...prev, savedItem]);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    // You could add a toast notification here
  };

  const getContentTypeIcon = (type) => {
    const contentType = contentTypes?.find(ct => ct?.id === type);
    return contentType ? contentType?.icon : FileText;
  };

  const getContentTypeColor = (type) => {
    const contentType = contentTypes?.find(ct => ct?.id === type);
    return contentType ? contentType?.color : 'bg-gray-500';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">AI Content Generator</h2>
        <p className="text-gray-600">Create compelling content for every stage of your marketing funnel</p>
      </div>
      {/* Content Type Selection */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Content Type</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {contentTypes?.map((type) => {
            const Icon = type?.icon;
            return (
              <motion.button
                key={type?.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedContentType(type?.id)}
                className={`p-4 rounded-lg text-center transition-all ${
                  selectedContentType === type?.id
                    ? `${type?.color} text-white shadow-lg`
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-6 h-6 mx-auto mb-2" />
                <span className="text-sm font-medium">{type?.label}</span>
              </motion.button>
            );
          })}
        </div>
      </div>
      {/* Funnel Stage Selection */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Funnel Stage</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {funnelStages?.map((stage) => (
            <motion.button
              key={stage?.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedStage(stage?.id)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                selectedStage === stage?.id
                  ? 'border-purple-500 bg-purple-50 text-purple-900' :'border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              <h4 className="font-semibold mb-2">{stage?.label}</h4>
              <p className="text-sm opacity-80">{stage?.description}</p>
            </motion.button>
          ))}
        </div>
      </div>
      {/* Custom Prompt */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Instructions (Optional)</h3>
        <textarea
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e?.target?.value)}
          placeholder="Provide specific instructions for content generation (e.g., tone, style, key points to include...)"
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>
      {/* Generate Button */}
      <div className="text-center">
        <button
          onClick={generateContent}
          disabled={isGenerating || !selectedContentType || !selectedStage}
          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-3 px-8 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 mx-auto"
        >
          {isGenerating ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Generating Content...</span>
            </>
          ) : (
            <>
              <Wand2 className="w-5 h-5" />
              <span>Generate AI Content</span>
            </>
          )}
        </button>
      </div>
      {/* Generated Content */}
      {generatedContent && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg p-6 shadow-sm"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <Lightbulb className="w-5 h-5 text-yellow-500" />
              <span>Generated Content Ideas</span>
            </h3>
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getContentTypeColor(generatedContent?.type)} text-white`}>
                {contentTypes?.find(ct => ct?.id === generatedContent?.type)?.label}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                {funnelStages?.find(fs => fs?.id === generatedContent?.stage)?.label}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            {generatedContent?.content?.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <h4 className="font-semibold text-gray-900 flex-1">{item?.title}</h4>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => copyToClipboard(item?.title + '\n\n' + item?.description)}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copy to clipboard"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => saveContent(item)}
                      className="p-2 text-blue-400 hover:text-blue-600 transition-colors"
                      title="Save content"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <p className="text-gray-700 mb-3">{item?.description}</p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center space-x-1">
                      <Target className="w-4 h-4" />
                      <span>{item?.call_to_action}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <BarChart3 className="w-4 h-4" />
                      <span>{item?.estimated_performance}</span>
                    </span>
                  </div>
                  <button className="text-purple-600 hover:text-purple-700 font-medium text-sm flex items-center space-x-1">
                    <Edit3 className="w-4 h-4" />
                    <span>Edit</span>
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="flex justify-center mt-6">
            <button
              onClick={generateContent}
              className="flex items-center space-x-2 text-purple-600 hover:text-purple-700 font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Generate More Ideas</span>
            </button>
          </div>
        </motion.div>
      )}
      {/* Saved Content */}
      {savedContent?.length > 0 && (
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span>Saved Content ({savedContent?.length})</span>
          </h3>

          <div className="space-y-3">
            {savedContent?.slice(-5)?.map((item) => {
              const Icon = getContentTypeIcon(item?.type);
              return (
                <div key={item?.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${getContentTypeColor(item?.type)}`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{item?.title}</h4>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(item.savedAt)?.toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {savedContent?.length > 5 && (
            <div className="text-center mt-4">
              <button className="text-purple-600 hover:text-purple-700 font-medium text-sm">
                View All Saved Content ({savedContent?.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentGenerator;